import React, { useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import { debounce } from 'lodash-es'
import { Global, css } from '@emotion/react'
import positionIcon from './assets/position.svg'

const styles = css`
  .amap-sug-result {
    height: 170px;
    overflow: auto;
    border: none !important;
    background: #fff !important;
    border-radius: 4px !important;
    box-shadow: 0 1px 10px -1px rgba(0, 0, 0, 0.1) !important;
  }
  .auto-item {
    margin-top: 12px;
    padding-left: 12px;
    white-space: nowrap;
    font-size: 14px;
    color: rgba(0, 0, 0, 0.65);
    cursor: pointer;
    &:hover {
      background-color: rgba(0, 86, 254, 0.1);
    }
  }
`

const inputStyle = {
  background: '#fff',
  boxShadow: '0 1px 10px -1px rgba(0,0,0,0.10)',
  fontSize: '0.8175rem',
}

const getLnglat = (location) => {
  if (location) {
    const { lnglat } = location
    if (lnglat) {
      const { lng, lat } = lnglat
      return [lng, lat]
    }
  }
  return []
}

const checkIsLngLat = (value) => {
  if (!value) return false
  return value.match(/^-?\d+(\.\d+)?,-?\d+(\.\d+)?$/)
}

const Search = ({
  amap,
  AMap,
  inputId,
  selectLocation,
  locationName,
  updateLocationName,
  getSelectLocation,
  icon,
  regeoCode,
  draggeLocation,
}) => {
  const [currentLnglat, setCurrentLnglat] = useState('')
  const [showFocus, setShowFocus] = useState(false)
  const onLnglatInput = (e) => {
    const { value } = e.target
    setCurrentLnglat(value)
    setShowFocus(checkIsLngLat(value))
  }

  const autoCompleteRef = useRef(null)
  const debouncedTrigger = useMemo(
    () =>
      debounce((v) => {
        if (!autoCompleteRef.current) return
        autoCompleteRef.current.value = v
        autoCompleteRef.current.dispatchEvent(new CustomEvent('input'))
      }, 500),
    []
  )

  // 记录当前信息窗体
  let currentInfoWindow = null

  const markerClick = (e, createWindowInfo) => {
    const { lnglat } = e
    if (createWindowInfo) {
      currentInfoWindow = new AMap.InfoWindow({
        offset: new AMap.Pixel(0, -30),
      })
      currentInfoWindow.open(amap, lnglat)
      currentInfoWindow.setContent(createWindowInfo(e, currentInfoWindow))
    }
  }

  const createWindowInfo = (e) => {
    const { lnglat } = e
    const div = document.createElement('div')
    const geocoder = new AMap.Geocoder({
      city: '全国',
      radius: 1000,
    })
    geocoder.getAddress(lnglat, (status, result) => {
      if (status === 'complete' && result.regeocode) {
        div.setAttribute(
          'style',
          'padding: 0.4375rem 0.75rem;font-size: 0.8125rem'
        )
        const span = document.createElement('span')
        span.innerText = result.regeocode.formattedAddress
        div.appendChild(span)
      }
    })
    return div
  }

  const addMark = (location, name) => {
    const { lat, lng } = location
    amap.clearMap()
    amap.setCenter([lng, lat])

    new AMap.Marker({
      map: amap,
      icon: icon(),
      offset: new AMap.Pixel(-13, -30),
      position: new AMap.LngLat(lng, lat),
      draggable: true,
    })
      .on('click', (e) => {
        markerClick(e, createWindowInfo)
      })
      .on('dragend', (e) => {
        if (currentInfoWindow) {
          currentInfoWindow.close()
        }
        const { lnglat } = e
        regeoCode(lnglat, '全国', updateLocationName)
        draggeLocation(e, true)
      })

    getSelectLocation({ lnglat: location })
    if (name) {
      updateLocationName(name)
    } else {
      regeoCode(location, '全国', updateLocationName)
    }
  }

  const addSearchEvent = () => {
    // Autocomplete 输入提示插件
    // PlaceSearch POI搜索插件
    AMap.plugin(['AMap.AutoComplete', 'AMap.PlaceSearch'], () => {
      const autoOptions = {
        city: '全国',
        input: inputId,
      }
      // 自动在页面相应的表单控件上监听输入并显示匹配结果
      const autoComplete = new AMap.Autocomplete(autoOptions)
      const placeSearch = new AMap.PlaceSearch({
        city: '全国',
        renderStyle: 'newpc',
        autoFitView: true,
      })
      AMap.Event.addListener(autoComplete, 'select', (e) => {
        placeSearch.setCity(e.poi.adcode)
        placeSearch.search(e.poi.name)
      })
      AMap.Event.addListener(autoComplete, 'error', () => {
        console.log('input搜索查询失败')
      })

      AMap.Event.addListener(placeSearch, 'complete', ({ poiList }) => {
        const { pois } = poiList
        if (pois.length > 0) {
          const poi = pois[0]
          const { location, name } = poi
          addMark(location, name)
        }
      })
    })
  }

  const focus = () => {
    console.log(currentLnglat)
    const [lng, lat] = currentLnglat.split(',').map(Number)
    const lnglat = { lng, lat }
    addMark(lnglat)
  }

  useEffect(() => {
    setCurrentLnglat(getLnglat(selectLocation).join(','))
    setShowFocus(false)
  }, [selectLocation])

  useEffect(() => {
    if (amap) {
      addSearchEvent()
    }
  }, [amap])

  return (
    <>
      <Global styles={styles} />
      <div className="w-4/5 sm:w-1/3 absolute top-4 left-4 flex flex-row items-center z-[100]">
        <input
          name="keyword"
          type="text"
          value={locationName || ''}
          className="form-input w-full pr-8 border-none z-[10]"
          placeholder="请输入地址搜索"
          style={inputStyle}
          onChange={(e) => {
            updateLocationName(e.target.value)
            debouncedTrigger(e.target.value)
          }}
        />
        <input
          id={inputId}
          className="absolute form-input w-full pr-8 border-none z-[9] opacity-0"
          ref={autoCompleteRef}
        />
      </div>
      <div className="w-4/5 sm:w-1/3 absolute top-14 left-4 flex flex-row items-center z-[100]">
        <input
          className="form-input w-full pr-8 border-none"
          placeholder="请输入经纬度搜索"
          style={inputStyle}
          value={currentLnglat}
          onChange={onLnglatInput}
        />
        <div
          className={clsx('absolute right-2', showFocus || 'hidden')}
          onClick={focus}
        >
          <img
            className="w-4 cursor-pointer opacity-60 hover:opacity-100 transition"
            src={positionIcon}
            alt="position"
          />
        </div>
      </div>
    </>
  )
}

export default Search
